import {
  useElement,
  useState,
  useStaleLayout,
  useRect,
  useEffect,
  useModel,
} from '@nebula.js/stardust';
import picassojs from 'picasso.js';
import picassoQ from 'picasso-plugin-q';
import picassoHammer from 'picasso-plugin-hammer';
import languagify from 'utils/languagify';

export default function supernova() {
  const picasso = picassojs();
  picasso.use(picassoQ);
  picasso.use(picassoHammer);

  picasso.formatter.remove('abs');
  picasso.formatter('abs', () => {
    const f = (v) => Math.abs(v);
    return f;
  });

  return {
    qae: {
      properties: {
        qHyperCubeDef: {
          qDimensions: [],
          qMeasures: [],
          qInitialDataFetch: [{ qWidth: 3, qHeight: 3000 }],
          qSuppressZero: false,
          qSuppressMissing: true,
        },
        showTitles: true,
        title: '',
        subtitle: '',
        footnote: '',
      },
      data: {
        targets: [
          {
            path: '/qHyperCubeDef',
            dimensions: {
              min: 1,
              max: 1,
            },
            measures: {
              min: 1,
              max: 2,
            },
          },
        ],
      },
    },
    component() {
      const element = useElement();
      const layout = useStaleLayout();
      const rect = useRect();
      const model = useModel();

      const [instance, setInstance] = useState();

      useEffect(() => {
        const p = picasso.chart({
          element,
          data: [],
          settings: {},
        });

        setInstance(p);

        return () => {
          p.destroy();
        };
      }, []);

      useEffect(() => {
        if (!instance) {
          return;
        }

        instance.update({
          data: [
            {
              type: 'q',
              key: 'qHyperCube',
              data: layout.qHyperCube,
            },
          ],
          settings: {
            formatters: {
              percent: {
                formatter: 'd3',
                type: 'number',
                format: ',.0%',
              },
            },

            scales: {
              y: {
                data: {
                  extract: {
                    field: 'qDimensionInfo/0',
                  },
                },
                invert: true,
                // paddingInner: 0.3,
                // paddingOuter: 0,
              },
              x: {
                data: {
                  fields: ['qMeasureInfo/0', 'qMeasureInfo/1'],
                  min: ({ data }) => -Math.max(...data.fields.map((f) => f.max())) * 1.3,
                },
                expand: 0.07,
                invert: false,
                paddingInner: 0.2,
              },
              c: {
                data: [0, 1],
                range: ['#4477AA', '#CC6677'],
                type: 'categorical-color',
              },
            },
            components: [
              {
                type: 'axis',
                dock: 'bottom',
                scale: 'x',
                formatter: {
                  type: 'abs',
                },
              },
              {
                type: 'axis',
                dock: 'left',
                scale: 'y',
              },
              {
                key: 'bars',
                type: 'box',
                data: {
                  extract: [
                    {
                      field: 'qDimensionInfo/0',
                      props: {
                        idx: 0,
                        start: 0,
                        end: { field: 'qMeasureInfo/0' },
                      },
                    },
                    {
                      field: 'qDimensionInfo/0',
                      props: {
                        idx: 1,
                        start: 0,
                        end: { field: 'qMeasureInfo/1' },
                      },
                    },
                  ],
                },
                settings: {
                  orientation: 'horizontal',
                  major: { scale: 'y' },
                  minor: {
                    start: (d) => d.resources.scale('x')(0),
                    end: (d) => d.resources.scale('x')(
                      // (d.datum.idx.value === 1 ? -1 : 1) * d.datum.end.value,
                      d.datum.end.value,
                    ),
                  },
                  box: {
                    width: 0.9,
                    fill(d) {
                      return d.resources.scale('c')(d.datum.idx.value);
                    },
                  },
                },
              },
              {
                type: 'labels',
                displayOrder: 2,
                settings: {
                  sources: [{
                    component: 'bars',
                    selector: 'rect',
                    strategy: {
                      type: 'bar',
                      settings: {
                        // direction: 'left',
                        direction: (node) => (node.data.end.value < 0 ? 'left' : 'right'),
                        fontSize: 10,
                        // align: 0.5,
                        // justify: 1,
                        labels: [{
                          placements: [{
                            position: 'outside',
                          }],
                          label: (node) => (node.data.end.value < 0 ? node.data.end.value * -1 : node.data.end.value),
                        }],
                        // fontSize: 10,
                      },
                    },
                  }],
                },
              },
              {
                type: 'grid-line',
                x: {
                  scale: 'x',
                },
              },
              {
                type: 'legend-cat',
                scale: {
                  data: ['2021', '2022'],
                  range: ['#4477AA', '#CC6677'],
                  type: 'categorical-color',
                },
                dock: 'right',
              },
            ],
            interactions: [
              {
                type: 'native',
                events: {
                },
              },
              {
                type: 'hammer',
                gestures: [
                  {
                    type: 'Tap',
                    events: {
                      tap(e) {
                        const chartBounds = element.getBoundingClientRect();
                        const cx = e.center.x - chartBounds.left;
                        const cy = e.center.y - chartBounds.top;
                        const shapes = this.chart.shapesAt({ x: cx, y: cy });
                        const values = shapes.map((shape) => shape.data.value);
                        model.selectHyperCubeValues('/qHyperCubeDef', 0, values, true);
                      },
                    },
                  },
                ],
              },
            ],
          },
        });
        instance.element.querySelector('[data-label="# of Journalist"]').textContent = languagify(instance.element.querySelector('[data-label="# of Journalist"]').textContent);
        instance.element.querySelector('[data-label="# of Media Outlets"]').textContent = languagify(instance.element.querySelector('[data-label="# of Media Outlets"]').textContent);
        instance.element.querySelector('[data-label="# of Men"]').textContent = languagify(instance.element.querySelector('[data-label="# of Men"]').textContent);
        instance.element.querySelector('[data-label="# of Women"]').textContent = languagify(instance.element.querySelector('[data-label="# of Women"]').textContent);
        instance.element.querySelector('[data-label="# of Non-Binary"]').textContent = languagify(instance.element.querySelector('[data-label="# of Non-Binary"]').textContent);
        instance.element.querySelector('[data-label="# of Supervisors"]').textContent = languagify(instance.element.querySelector('[data-label="# of Supervisors"]').textContent);
        instance.element.querySelector('[data-label="# of Full-time"]').textContent = languagify(instance.element.querySelector('[data-label="# of Full-time"]').textContent);
        instance.element.querySelector('[data-label="# of Part-time"]').textContent = languagify(instance.element.querySelector('[data-label="# of Part-time"]').textContent);
        instance.element.querySelector('[data-label="# of Interns"]').textContent = languagify(instance.element.querySelector('[data-label="# of Interns"]').textContent);
        instance.element.querySelector('[data-label="# of Indigenous"]').textContent = languagify(instance.element.querySelector('[data-label="# of Indigenous"]').textContent);
        instance.element.querySelector('[data-label="# of Asian"]').textContent = languagify(instance.element.querySelector('[data-label="# of Asian"]').textContent);
        instance.element.querySelector('[data-label="# of Black"]').textContent = languagify(instance.element.querySelector('[data-label="# of Black"]').textContent);
        instance.element.querySelector('[data-label="# of Latin"]').textContent = languagify(instance.element.querySelector('[data-label="# of Latin"]').textContent);
        instance.element.querySelector('[data-label="# of Middle Eastern"]').textContent = languagify(instance.element.querySelector('[data-label="# of Middle Eastern"]').textContent);
        instance.element.querySelector('[data-label="# of White"]').textContent = languagify(instance.element.querySelector('[data-label="# of White"]').textContent);
        instance.element.querySelector('[data-label="# of Mixed Race"]').textContent = languagify(instance.element.querySelector('[data-label="# of Mixed Race"]').textContent);
        instance.element.querySelector('[data-label="# of Unknown"]').textContent = languagify(instance.element.querySelector('[data-label="# of Unknown"]').textContent);
      }, [layout, instance]);

      useEffect(() => {
        if (!instance) {
          return;
        }
        instance.update();
      }, [rect.width, rect.height, instance]);
    },
  };
}
